import { useCallback, useMemo, useState, useSyncExternalStore } from 'react';
import type { Dispatch, SetStateAction } from 'react';

import { describeError } from '@/utils/errors';
import { useEventCallback } from './index';

declare global {
  interface WindowEventMap {
    'local-storage': CustomEvent<{ key: string }>;
  }
}

type UseLocalStorageOptions<T> = {
  serializer?: (value: T) => string;
  deserializer?: (value: string) => T;
};

const IS_SERVER = typeof window === 'undefined';

export function useLocalStorage<T>(
  key: string,
  initialValue: T | (() => T),
  options: UseLocalStorageOptions<T> = {}
): [T, Dispatch<SetStateAction<T>>, () => void] {
  const [fallbackValue] = useState<T>(() =>
    initialValue instanceof Function ? initialValue() : initialValue
  );
  const getInitialValue = useCallback((): T => fallbackValue, [fallbackValue]);

  const serializer = useCallback<(value: T) => string>(
    (value) => {
      if (options.serializer) {
        return options.serializer(value);
      }

      return JSON.stringify(value);
    },
    [options]
  );

  const deserializer = useCallback<(value: string) => T>(
    (value) => {
      if (options.deserializer) {
        return options.deserializer(value);
      }
      // Support 'undefined' as a value
      if (value === 'undefined') {
        return undefined as unknown as T;
      }

      const defaultValue = getInitialValue();

      let parsed: unknown;
      try {
        parsed = JSON.parse(value);
      } catch (error) {
        console.error('Error parsing JSON:', describeError(error));
        return defaultValue; // Return initialValue if parsing fails
      }

      return parsed as T;
    },
    [options, getInitialValue]
  );

  const readRawValue = useCallback((): string | null => {
    if (IS_SERVER) {
      return null;
    }

    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      console.warn(`Error reading localStorage key “${key}”:`, describeError(error));
      return null;
    }
  }, [key]);

  const getServerSnapshot = useCallback((): null => null, []);

  const subscribe = useCallback(
    (notify: () => void) => {
      if (IS_SERVER) {
        return () => undefined;
      }

      const handleStorageChange = (event: StorageEvent | CustomEvent<{ key: string }>) => {
        const changedKey = event instanceof CustomEvent ? event.detail.key : event.key;
        if (changedKey && changedKey !== key) {
          return;
        }
        notify();
      };

      window.addEventListener('storage', handleStorageChange);
      window.addEventListener('local-storage', handleStorageChange);

      return () => {
        window.removeEventListener('storage', handleStorageChange);
        window.removeEventListener('local-storage', handleStorageChange);
      };
    },
    [key]
  );

  const rawValue = useSyncExternalStore(subscribe, readRawValue, getServerSnapshot);
  const storedValue = useMemo(
    (): T => (rawValue === null ? getInitialValue() : deserializer(rawValue)),
    [deserializer, getInitialValue, rawValue]
  );

  const setValue: Dispatch<SetStateAction<T>> = useEventCallback((value) => {
    if (IS_SERVER) {
      console.warn(
        `Tried setting localStorage key “${key}” even though environment is not a client`
      );
      return;
    }

    try {
      const currentRawValue = readRawValue();
      const currentValue =
        currentRawValue === null ? getInitialValue() : deserializer(currentRawValue);
      const newValue = value instanceof Function ? value(currentValue) : value;
      window.localStorage.setItem(key, serializer(newValue));
      window.dispatchEvent(new CustomEvent('local-storage', { detail: { key } }));
    } catch (error) {
      console.warn(`Error setting localStorage key “${key}”:`, describeError(error));
    }
  });

  const removeValue = useEventCallback(() => {
    if (IS_SERVER) {
      console.warn(
        `Tried removing localStorage key “${key}” even though environment is not a client`
      );
      return;
    }

    try {
      window.localStorage.removeItem(key);
      window.dispatchEvent(new CustomEvent('local-storage', { detail: { key } }));
    } catch (error) {
      console.warn(`Error removing localStorage key “${key}”:`, describeError(error));
    }
  });

  return [storedValue, setValue, removeValue];
}
